import React from 'react'
import { useTranslation } from 'react-i18next'
import { Modal, Highlight } from '@pandiafi/components'
import { Button } from '@pandiafi/design-system'
import { DateTime } from 'luxon'

import { useCurrentContract } from '../../hooks/useCurrentContract/useCurrentContract'
import { useAPIRemoveRightOfAccess } from '../../api/apiHooks/useApiRemoveRightOfAccess'
import type { RightOfAccess } from '../../api/apiHooks/useAPIGetRightsOfAccess'

interface ReleaseAccessModalProps {
  rightOfAccess: RightOfAccess | null
  onClose: () => void
}

export const ReleaseAccessModal: React.FC<ReleaseAccessModalProps> = ({
  rightOfAccess,
  onClose,
}) => {
  const { t } = useTranslation('gymBooking')
  const { currentContractId } = useCurrentContract()

  const today = DateTime.now()

  const removeRightOfAccessMutation =
    useAPIRemoveRightOfAccess(currentContractId)

  if (!rightOfAccess) return null

  const startDate = DateTime.fromISO(rightOfAccess.startDate).startOf('day')
  const isFutureAccess = startDate > today.startOf('day')

  const handleClose = () => {
    onClose()
  }

  const handleRemove = async () => {
    if (!rightOfAccess) {
      return
    }

    removeRightOfAccessMutation.mutate(
      {
        rightOfAccessId: rightOfAccess.id,
      },
      {
        onSuccess: () => {
          onClose()
        },
      }
    )
  }

  return (
    <Modal
      show
      onClose={handleClose}
      title={t('modal.releaseTitle')}
      headerElement={<Highlight>{t('modal.headerRelease')}</Highlight>}
    >
      <Modal.Content>
        <div className="text-lg mb-4">
          <span className="font-bold">{t('releaseModal.confirmQuestion')}</span>
        </div>
        <p className="mb-4">
          {t('releaseModal.accessDetails', {
            address: `${rightOfAccess.buildingPartAddress.streetName} ${rightOfAccess.buildingPartAddress.streetNumber}`,
            startDate: startDate.toFormat('dd.MM.yyyy'),
          })}
        </p>
        {isFutureAccess ? (
          <p className="mb-4">{t('releaseModal.validUntilImmediate')}</p>
        ) : (
          <p className="mb-4">
            {t('releaseModal.validUntilPrefix')}{' '}
            {today.endOf('month').toFormat('dd.MM.yyyy')}{' '}
            {t('releaseModal.validUntilSuffix')}
          </p>
        )}
      </Modal.Content>

      <Modal.Buttons>
        <Button variant="cancel" onClick={handleClose}>
          {t('modal.cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleRemove}
          disabled={removeRightOfAccessMutation.isPending}
          loading={removeRightOfAccessMutation.isPending}
        >
          {t('modal.confirm')}
        </Button>
      </Modal.Buttons>
    </Modal>
  )
}
